import React, { useEffect, useState } from "react";
import { Card, CardBody, Col } from "reactstrap";
import PropTypes from "prop-types";
import MonthlyOrdersTableFilter from "./MonthlyOrdersTableFilter";
import MonthlyOrdersTableByOrderView from "./MonthlyOrdersTableByOrderView";
import MonthlyOrdersTableBySubscriptionView from "./MonthlyOrdersTableBySubscriptionView";
import { useSearchParams } from "react-router-dom";
import MonthlyParkingStatisticContainer from "./MonthlyParkingStatisticContainer";
import MonthlyOrdersTableByLocationView from "./MonthlyOrdersTableByLocationView";

const MonthlyOrdersTable = ({ title, orders, reloadTime }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState({
        succeed: searchParams.get("succeed") === "1",
        failed: searchParams.get("failed") === "1",
        pending: searchParams.get("pending") === "1",
        onHold: searchParams.get("onHold") === "1",
        stripe: searchParams.get("stripe") === "1",
        cod: searchParams.get("cod") === "1",
        cheque: searchParams.get("cheque") === "1",
        qbo: searchParams.get("qbo") === "1",
        showBarcode: false,
        pagination: true,
    });
    const initialSearch = searchParams.get("q") || "";
    const [search, setSearch] = useState(initialSearch);
    const [appliedSearch, setAppliedSearch] = useState(initialSearch);
    const [pendingFilters, setPendingFilters] = useState(filters);
    const initialView = searchParams.get("view") || "byOrder";
    const [tableView, setTableView] = useState(initialView);
    const [expandedLocation, setExpandedLocation] = useState(null);
    const [orderPage, setOrderPage] = useState(1);
    const [locationPages, setLocationPages] = useState({});
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [sortConfig, setSortConfig] = useState({ key: "date_created", direction: "desc" });
    const [dateFilteredOrders, setDateFilteredOrders] = useState([]);
    const pageSize = 100;

    useEffect(() => {
        const q = appliedSearch.trim();
        const params = {};

        if (q) params.q = q;
        if (tableView) params.view = tableView;

        ["succeed", "failed", "pending", "onHold"].forEach(key => {
            if (filters[key]) params[key] = "1";
        });

        ["stripe", "cod", "cheque", "qbo"].forEach(key => {
            if (filters[key]) params[key] = "1";
        });

        setSearchParams(params);
    }, [
        search,
        tableView,
        filters.succeed,
        filters.failed,
        filters.pending,
        filters.onHold,
        filters.stripe,
        filters.cod,
        filters.cheque,
        filters.qbo
    ]);

    useEffect(() => {
        if (!orders?.length) return;

        const filtered = orders.map(loc => {
            const filteredOrders = (loc.orders || []).filter(o => {
                if (!dateRange.start || !dateRange.end) return true;
                const orderDate = new Date(o.date_created);
                const orderDateStr = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, "0")}-${String(orderDate.getDate()).padStart(2, "0")}`;
                return orderDateStr >= dateRange.start && orderDateStr <= dateRange.end;
            });
            return { ...loc, orders: filteredOrders };
        });

        setDateFilteredOrders(filtered);
    }, [orders, dateRange]);

    const sortedOrders = (list) => {
        if (!sortConfig.key) return list;
        return [...list].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === "date_created") {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }
            if (sortConfig.key === "total") {
                aVal = Number(aVal) || 0;
                bVal = Number(bVal) || 0;
            }
            if (typeof aVal === "string") aVal = aVal.toLowerCase();
            if (typeof bVal === "string") bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    };

    const filterOrders = (ordersSubset) => {
        return ordersSubset.filter((o) => {
            if (o.status === "deleted") return false;

            // status filters
            const statusFilters = [];
            if (filters.succeed) statusFilters.push("completed", "active");
            if (filters.failed) statusFilters.push("failed", "cancelled");
            if (filters.pending) statusFilters.push("pending");
            if (filters.onHold) statusFilters.push("on-hold");
            if (statusFilters.length && !statusFilters.includes(String(o.status).toLowerCase())) return false;

            // payment filters
            const paymentFilters = [];
            if (filters.stripe) paymentFilters.push("stripe");
            if (filters.cod) paymentFilters.push("cod");
            if (filters.cheque) paymentFilters.push("cheque");
            if (filters.qbo) paymentFilters.push("qbo");
            if (paymentFilters.length > 0) {
                const match = paymentFilters.some((pf) => {
                    const pt = o.payment_type?.toLowerCase() || "";
                    return pf === "stripe" || pf === "qbo" ? pt.startsWith(pf) : pt === pf;
                });
                if (!match) return false;
            }

            // search
            const q = search.trim().toLowerCase();
            if (q) {
                return (
                    (o.locationName || "").toLowerCase().includes(q) ||
                    ((o.customer_first_name + " " + o.customer_last_name) || "").toLowerCase().includes(q) ||
                    (o.customer_email || "").toLowerCase().includes(q) ||
                    (o.customer_company_name || "").toLowerCase().includes(q) ||
                    (o.customer_phone || "").toLowerCase().includes(q) ||
                    (o.license_plates || "").toLowerCase().includes(q) ||
                    (o.check_number || "").toLowerCase().includes(q) ||
                    (o.car_models || "").toLowerCase().includes(q)
                );
            }

            return true;
        });
    };

    return (
        <Col xl={12}>
            <Card className="accordion-gradient-border">
                <CardBody>
                    {title && <h5 className="mb-3">{title}</h5>}

                    <MonthlyOrdersTableFilter
                        search={search}
                        setSearch={setSearch}
                        setOrderPage={setOrderPage}
                        setDateRange={setDateRange}
                        orders={dateFilteredOrders}
                        filterOrders={filterOrders}
                        filters={pendingFilters}
                        setFilters={setFilters}
                        setPendingFilters={setPendingFilters}
                        tableView={tableView}
                        setTableView={setTableView}
                        onRefresh={reloadTime}
                        onApply={() => {
                            setFilters(pendingFilters);
                            setAppliedSearch(search);
                            setOrderPage(1);
                        }}
                    />

                    {/* Show By Order */}
                    {tableView === "byOrder" && (
                        <MonthlyOrdersTableByOrderView
                            orders={dateFilteredOrders}
                            filterOrders={filterOrders}
                            filters={filters}
                            orderPage={orderPage}
                            setOrderPage={setOrderPage}
                            pageSize={pageSize}
                            reloadTime={reloadTime}
                            sortedOrders={sortedOrders}
                            setSortConfig={setSortConfig}
                        />
                    )}

                    {/* Show By Subscription */}
                    {tableView === "bySubscription" && (
                        <MonthlyOrdersTableBySubscriptionView
                            orders={dateFilteredOrders}
                            search={search}
                            filters={filters}
                            filterOrders={filterOrders}
                            expandedLocation={expandedLocation}
                            setExpandedLocation={setExpandedLocation}
                            locationPages={locationPages}
                            setLocationPages={setLocationPages}
                            pageSize={pageSize}
                            setSortConfig={setSortConfig}
                            sortedOrders={sortedOrders}
                            onRefresh={reloadTime}
                        />
                    )}

                    {/* Show By Location */}
                    {tableView === "byLocation" && (
                        <MonthlyOrdersTableByLocationView
                            orders={dateFilteredOrders}
                            search={search}
                            filters={filters}
                            filterOrders={filterOrders}
                            expandedLocation={expandedLocation}
                            setExpandedLocation={setExpandedLocation}
                            locationPages={locationPages}
                            setLocationPages={setLocationPages}
                            pageSize={pageSize}
                            setSortConfig={setSortConfig}
                            sortedOrders={sortedOrders}
                            onRefresh={reloadTime}
                        />
                    )}

                    {/* Line Stats */}
                    <MonthlyParkingStatisticContainer
                        orders={dateFilteredOrders}
                        dateRange={dateRange}
                        filterOrders={filterOrders}
                        filters={filters}
                    />
                </CardBody>
            </Card>
        </Col>
    );
};

MonthlyOrdersTable.propTypes = {
    title: PropTypes.string,
    orders: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string,
            orders: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    date_created: PropTypes.string,
                    payment_type: PropTypes.string,
                    total: PropTypes.number,
                    status: PropTypes.string,
                    customer_name: PropTypes.string,
                })
            ),
        })
    ).isRequired,
};

export default MonthlyOrdersTable;